import {
  Instance,
  types,
  flow,
  getEnv,
  cast,
  destroy,
  getRoot,
  getSnapshot,
  applySnapshot,
} from 'mobx-state-tree'
import { Environment } from '../environment'
import { Topic } from '../Topic'
import { Question } from '../Question'
import { Quiz } from '../Quiz'
import { TopicHelper } from '../../helpers/TopicHelper'
import { QuizHelper } from '../../helpers/QuizHelper'
import { QuestionHelper } from '../../helpers/QuestionHelper'
import { ContentCreationApi } from '../../services/api-objects/ContentCreationApi'
import { WorkflowApi } from '../../services/api-objects/WorkflowApi'
import { flatten } from 'ramda'

const _ = require('lodash')

export const ContentCreationStoreModel = types
  .model('ContentCreationStore')
  .props({
    status: types.optional(types.enumeration(['idle', 'pending', 'done', 'error']), 'idle'),
    isPublishingContent: types.optional(types.boolean, false),
    isPublishingResources: types.optional(types.boolean, false),
    isPublishingImages: types.optional(types.boolean, false),
    topic: types.maybeNull(Topic),
    topics: types.maybe(types.array(Topic)),
    quiz: types.maybeNull(Quiz),
    question: types.maybeNull(Question),
    importingQuestions: types.maybe(types.array(Question)),
    importingQuizzes: types.maybe(types.array(Quiz)),
    importingFilename: types.maybeNull(types.string),
    showFilePicker: types.frozen(),
  })
  .actions((self) => ({
    setStatus(value?: 'idle' | 'pending' | 'done' | 'error') {
      self.status = value || 'idle'
    },
    setImportingQuestions(q) {
      self.importingQuestions = cast([])
      self.importingQuestions = q
    },
    setImportingQuizzes(q) {
      self.importingQuizzes = q
    },
    removeImportedQuiz(q) {
      destroy(q)
    },
    removeImportedQuestion(q) {
      destroy(q)
    },
    setShowFilePicker(status) {
      self.showFilePicker = status
    },
    setImportingFilename(filename: string) {
      self.importingFilename = filename
    },
    setIsPublishingContent(value: boolean) {
      self.isPublishingContent = value
    },
    setIsPublishingResources(value: boolean) {
      self.isPublishingResources = value
    },
    setIsPublishingImages(value: boolean) {
      self.isPublishingImages = value
    },
    setTopic(topic: Instance<typeof Topic> | null) {
      self.topic = topic
    },
    setTopics(topics) {
      self.topics = [] as any
      self.topics = topics as any
    },
    setQuiz(quiz: Instance<typeof Quiz> | null) {
      self.quiz = quiz
    },
    setQuestion(question: Instance<typeof Question> | null) {
      self.question = question
    },
  }))
  .views((self) => ({
    get environment() {
      return getEnv(self) as Environment
    },
    get cardNames() {
      return flatten([
        (self.importingQuizzes || [])?.map((quiz) => {
          return quiz.cardNames
        }),
        self.quiz?.cardNames,
        (self.topics || [])?.map((topic) => {
          return topic.cardNames
        }),
      ]).filter((c) => Boolean(c))
    },
    get importedQuestionErrors() {
      return flatten(self.importingQuestions?.map((qu) => qu.allErrors) || [])
    },
    get importedQuizzesErrors() {
      return flatten(self.importingQuizzes?.map((qu) => qu.allErrors) || [])
    },
    get selectedTopic() {
      return self.topics?.find((topic) => Boolean(topic?.selected))
    },
    get selectedQuestion() {
      return self.importingQuestions?.find((question) => Boolean(question?.selected))
    },
    get selectedQuiz() {
      return self.importingQuizzes?.find((quiz) => Boolean(quiz?.selected))
    },
    get isLoading() {
      return self.status === 'pending'
    },
    get isPublishing() {
      return self.isPublishingContent || self.isPublishingImages || self.isPublishingResources
    },
  }))
  .actions((self) => ({
    resetContentStore(fromPath = false) {
      self.status = 'idle'
      self.isPublishingContent = false
      self.isPublishingImages = false
      self.isPublishingResources = false
      self.importingFilename = null
      self.topic = null
      self.quiz = null
      self.question = null
      self.importingQuestions = cast([])
      self.importingQuizzes = cast([])
      self.topics = cast([])
      if (fromPath) {
        return
      }
      // reset paths / cards / workflow
      const root = getRoot(self) as any
      const { pathStore, cardStore } = root
      cardStore?.reset()
      const { workflow, path } = pathStore
      if (workflow && !path) {
        pathStore.deleteWorkflow()
      }
      if (path) {
        pathStore.deletePath()
      }
    },
  }))
  .actions((self) => ({
    selectTopic(topic) {
      self.topics?.map((t) => t.deselect())
      topic.select()
    },
    selectQuiz(quiz) {
      const sel = self.importingQuizzes?.find((t) => Boolean(t.selected))
      if (sel) {
        sel.deselect()
      }
      quiz.select()
    },
    removeTopicFromImport(topicToDelete: Instance<typeof Topic>) {
      // can only remove if we have at least 1 remaining
      if (self.topics && self.topics.length > 1) {
        if (topicToDelete.selected) {
          const unselected = (topic) => !topic.selected
          const next = _.find(self.topics, unselected, self.topics.indexOf(topicToDelete))
          const last = _.findLast(self.topics, unselected, self.topics.indexOf(topicToDelete) - 1)
          if (next) {
            this.selectTopic(next)
          } else if (last) {
            this.selectTopic(last)
          }
        }
        destroy(topicToDelete)
      }
    },
    publishContent: flow(function* () {
      self.setStatus('pending')
      self.setIsPublishingContent(true)
      const snapshot = getSnapshot(self)
      try {
        let resp
        if (self.topics && self.topics?.length > 0) {
          resp = yield ContentCreationApi.publishContent({
            topic: self.topics.map((t) => t.toPayload()),
          })
          // check for workflow and update object_ids
          const root = getRoot(self) as any
          const { pathStore } = root
          const { workflow } = pathStore
          if (self.topics && self.topics.length > 0) {
            resp.data.topic.map((topic, index) => {
              const wf = self.topics!.length === 1 ? workflow : self.topics![index]?.workflow
              // if imported topic has a workflow added, and workflow is not empty -> create a new one
              if (self.topics!.length > 1 && wf && wf.nodes.length > 0) {
                wf.updateTopicIDs(topic.topic_id)
                wf.prepareForPublish()
                WorkflowApi.createWorkflow(wf, topic.topic_id, 'Topic')
              } else if (wf && wf.nodes.length > 0) {
                // else if single topic and workflow found - update ids
                const topicID = resp.data.topic[0].topic_id
                workflow.updateTopicIDs(topicID)
                if (workflow) {
                  workflow.prepareForPublish()
                }
                // check if editing topic / workflow
                if (self.topics![0].topic_id && self.topics![0].workflow_id) {
                  WorkflowApi.editWorkflow(workflow, self.topics![0].workflow_id)
                } else {
                  WorkflowApi.createWorkflow(workflow, topicID, 'Topic')
                }
              }
            })

            // map for each published question/quiz/topic id in the imported topic set
            // for updating images / resources
            self.topics.map((topic, index) => {
              topic.set('topic_id', resp.data.topic[index].topic_id)
              topic.quizzes?.map((quiz, index2) => {
                quiz.set('quiz_id', resp.data.topic[index].quizzes[index2].quiz_id)
                quiz.questions
                  ?.filter((q) => !q.removing && q?.question_type !== 'Card')
                  .map((question, index3) => {
                    question.set(
                      'question_id',
                      resp.data.topic[index].quizzes[index2].questions[index3].question_id,
                    )
                  })
              })
            })
          }
        } else if (self.importingQuizzes?.length) {
          self.importingQuizzes.map((quiz) => {
            quiz?.questions
              ?.filter((q) => !q.removing)
              .map((qu, index) => {
                qu.setOrder(self.quiz?.keep_item_order ? index + 1 : null)
              })
          })
          resp = yield ContentCreationApi.publishContent({
            quiz: self.importingQuizzes?.map((quiz) => quiz.toPayload()),
          })

          self.importingQuizzes.map((quiz, idx) => {
            quiz.set('quiz_id', resp.data.quiz[idx].quiz_id)
            quiz?.questions
              ?.filter((q) => !q.removing && q.question_type !== 'Card')
              .map((q, index) => {
                q.set('question_id', resp.data.quiz[idx].questions[index]?.question_id)
              })
          })
        } else if (self.quiz) {
          self.quiz?.questions
            ?.filter((q) => !q.removing)
            .map((qu, index) => {
              qu.setOrder(self.quiz?.keep_item_order ? index + 1 : null)
            })
          resp = yield ContentCreationApi.publishContent({ quiz: [self.quiz.toPayload()] })

          // update ids after publishing
          self.quiz.set('quiz_id', resp.data.quiz[0].quiz_id)
          self.quiz?.questions
            ?.filter((q) => !q.removing && q.question_type !== 'Card')
            .map((q, index) => {
              q.set('question_id', resp.data.quiz[0].questions[index]?.question_id)
            })
        } else if (self.importingQuestions?.length) {
          resp = yield ContentCreationApi.publishContent({
            question: self.importingQuestions.map((qu) => qu.toPayload()),
          })
          self.importingQuestions.map((qu, idx) =>
            qu.set('question_id', resp.data.question[idx]?.question_id),
          )
        } else if (self.question) {
          resp = yield ContentCreationApi.publishContent({ question: [self.question.toPayload()] })
          // update ids after publishing
          self.question.set('question_id', resp.data.question[0]?.question_id)
        }

        return resp.data
      } catch (e) {
        console.log('err', e)
        self.setStatus('error')
        // capture sentry breadcrumb - know if resources / images / content failed
        const err = {
          error: true,
          contentPublished: !self.isPublishingContent,
          resourcesPublished: !self.isPublishingResources,
          imagesPublished: !self.isPublishingImages,
          message: !self.isPublishingContent
            ? 'Your content was published successfully, but one or more resources or cover images failed to upload.'
            : 'There was an error publishing your content. Please try again, and if this continues to occur please reach out to support@trivie.com',
        }

        // Content publish failed - apply snapshot before publish
        if (self.isPublishingContent) {
          applySnapshot(self, snapshot)
        }
        self.setIsPublishingContent(false)
        self.setIsPublishingResources(false)
        self.setIsPublishingImages(false)
        return err
      }
    }),
    publishImages: flow(function* () {
      if (self.topics && self.topics?.length > 0) {
        for (let i = 0; i < self.topics?.length; i++) {
          yield TopicHelper.publishImages(self.topics[i])
          if (self.topics[i] && self.topics[i].quizzes?.length) {
            // @ts-ignore
            for (let j = 0; j < self.topics[i].quizzes?.length; j++) {
              // @ts-ignore
              yield QuizHelper.publishImages(self.topics[i].quizzes[j])
            }
          }
        }
      } else if (self.importingQuizzes && self.importingQuizzes.length > 0) {
        for (let i = 0; i < self.importingQuizzes?.length; i++) {
          yield QuizHelper.publishImages(self.importingQuizzes[i])
        }
      } else if (self.quiz) {
        yield QuizHelper.publishImages(self.quiz)
      } else if (self.importingQuestions && self.importingQuestions.length > 0) {
        for (let i = 0; i < self.importingQuestions?.length; i++) {
          yield QuestionHelper.publishImage(self.importingQuestions[i])
        }
      } else if (self.question?.question_id) {
        yield QuestionHelper.publishImage(self.question)
      }
    }),
    publishResources: flow(function* () {
      if (self.topics && self.topics?.length > 0) {
        for (let i = 0; i < self.topics?.length; i++) {
          yield TopicHelper.publishResources(self.topics[i])
          if (self.topics[i] && self.topics[i].quizzes?.length) {
            // @ts-ignore
            for (let j = 0; j < self.topics[i].quizzes?.length; j++) {
              // @ts-ignore
              yield QuizHelper.publishResources(self.topics[i].quizzes[j])
            }
          }
        }
      } else if (self.importingQuizzes && self.importingQuizzes.length > 0) {
        for (let i = 0; i < self.importingQuizzes?.length; i++) {
          yield QuizHelper.publishResources(self.importingQuizzes[i])
        }
      } else if (self.quiz) {
        yield QuizHelper.publishResources(self.quiz)
      }
    }),
  }))

export interface ContentCreationStoreModelType
  extends Instance<typeof ContentCreationStoreModel.Type> {}
